import { CheckCircle } from '@untitledui/icons';
import { useEffect, useRef, useState } from 'react';

import { PipelineIcon } from '@/lib/crm/utils/icons/CustomIcons';
import { cx } from '@/utils/cx';

const DEFAULT_GENERATING_MESSAGE = ['Setting things up…'];

interface GeneratingActionsIndicatorProps {
  /** Whether onboarding is still actively running */
  isActive: boolean;
  /**
   * The first-actions step is parked WAITING on the user creating a deal
   * pipeline. This is not active work — render a distinct, static informational
   * state rather than the "generating" spinner.
   */
  waitingForPipeline?: boolean;
  /** Messages to cycle through */
  messages?: string[];
  /**
   * Compact inline rendering for the page-title row (titleTrailing slot):
   * no full-width block, no border, smaller text.
   */
  inline?: boolean;
}

/**
 * Indicator for the onboarding first-actions lifecycle. Three states:
 * - waitingForPipeline: parked on the user to create their deal pipeline (static, informational)
 * - isActive: actions are actively generating (spinner + cycling messages)
 * - otherwise: actions are ready (success)
 */
export function GeneratingActionsIndicator({
  isActive,
  waitingForPipeline = false,
  messages = DEFAULT_GENERATING_MESSAGE,
  inline = false,
}: GeneratingActionsIndicatorProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [textWidth, setTextWidth] = useState<number | undefined>();

  useEffect(() => {
    // Only run the cycling animation when active and there are multiple messages
    if (!isActive || messages.length <= 1) {
      setIsVisible(true);
      setMessageIndex(0);
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const interval = setInterval(() => {
      // Fade out
      setIsVisible(false);

      // After fade out, change message and fade in
      timeoutId = setTimeout(() => {
        setMessageIndex(prev => (prev + 1) % messages.length);
        setIsVisible(true);
      }, 300);
    }, 3000);

    return () => {
      clearInterval(interval);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isActive, messages]);

  // Measure text width from hidden measurer span. useEffect (not useLayoutEffect)
  // so the browser paints the old width first, enabling the CSS transition.
  useEffect(() => {
    if (measureRef.current) {
      setTextWidth(measureRef.current.offsetWidth);
    }
  }, [messageIndex, messages]);

  // Block mode centers a full-width band with a top border; inline mode is a
  // compact chip for the page-title row (titleTrailing slot).
  const containerClass = inline
    ? 'flex items-center'
    : 'flex items-center justify-center px-4 py-4 border-t border-[var(--color-border-secondary)]';
  const textClass = inline ? 'text-[13px] font-medium' : 'text-sm font-medium';
  const iconClass = inline ? 'size-4' : 'size-5';

  if (waitingForPipeline) {
    // Parked on the user to create their deal pipeline — not active work, so no
    // spinner. A static, informational state that explains the wait.
    return (
      <div className={containerClass} role='status' aria-live='polite'>
        <div
          className={cx(
            'flex items-center',
            inline ? 'gap-2' : 'max-w-[260px] gap-3'
          )}
        >
          <PipelineIcon
            className={cx(
              iconClass,
              'shrink-0 text-[var(--color-text-tertiary)]'
            )}
            aria-hidden='true'
          />
          <span className={cx(textClass, 'text-[var(--color-text-secondary)]')}>
            We&apos;ll surface your first actions once your deal pipeline is set
            up.
          </span>
        </div>
      </div>
    );
  }

  if (!isActive) {
    // Success state - imports complete
    return (
      <div className={containerClass} role='status' aria-live='polite'>
        <div className={cx('flex items-center', inline ? 'gap-2' : 'gap-3')}>
          <CheckCircle
            className={cx(iconClass, 'text-[var(--color-success-600)]')}
            aria-hidden='true'
          />
          <span className={cx(textClass, 'text-fg-secondary')}>
            Your first actions are ready
          </span>
        </div>
      </div>
    );
  }

  const currentMessage = messages[messageIndex % messages.length];

  // Active state - onboarding in progress
  return (
    <div className={cx('relative', containerClass)}>
      {/* Hidden measurer — absolutely positioned so it's unconstrained */}
      <span
        ref={measureRef}
        className={cx(textClass, 'whitespace-nowrap')}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
        aria-hidden='true'
      >
        {currentMessage}
      </span>

      <div className={cx('flex items-center', inline ? 'gap-2' : 'gap-3')}>
        <svg
          className={cx(
            iconClass,
            'animate-spin shrink-0 text-[var(--color-brand-500)]'
          )}
          viewBox='0 0 24 24'
          fill='none'
        >
          <circle
            className='opacity-25'
            cx='12'
            cy='12'
            r='10'
            stroke='currentColor'
            strokeWidth='3'
          />
          <path
            className='opacity-75'
            fill='currentColor'
            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
          />
        </svg>
        <div
          className='overflow-hidden transition-[width] duration-300 ease-out'
          style={textWidth ? { width: `${textWidth}px` } : undefined}
        >
          <span
            className={cx(
              textClass,
              'whitespace-nowrap transition-opacity duration-300 text-[var(--color-text-secondary)]',
              isVisible ? 'opacity-100' : 'opacity-0'
            )}
          >
            {currentMessage}
          </span>
        </div>
      </div>
    </div>
  );
}
